import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type PatchRoleInput, type RoleEntity } from '@magimp/shared';
import { usePatchRole } from '@magimp/shared-hooks';
import { Button, Input, Textarea, FormRow } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { RoleArchiveConfirm, RoleRestoreConfirm } from './RoleArchiveRestoreConfirm.js';

// UI form schema is decoupled from the API `PatchRoleInputSchema`: the form
// stores `description` as a plain string (empty = clear) so an empty Textarea
// does not fail the API schema's `.min(1)` rule before onSubmit can convert
// `''` to `null`. The conversion happens in onSubmit; the API contract is
// preserved on the wire.
const createRoleEditFormSchema = (requiredMessage: string) =>
  z.object({
    displayName: z.string().min(1, requiredMessage).max(64),
    description: z.string().max(280),
  });
type RoleEditFormValues = z.infer<ReturnType<typeof createRoleEditFormSchema>>;

export function RoleDetailsTab({
  role,
  disabled,
  archiveBlocked,
  onDirtyChange,
  onBusyChange,
}: {
  role: RoleEntity;
  disabled: boolean;
  archiveBlocked: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation(['roles', 'common', 'validation']);
  const patch = usePatchRole(role.id);
  const showToast = useToastStore((s) => s.show);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const schema = useMemo(() => createRoleEditFormSchema(t('validation:required')), [t]);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<RoleEditFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: role.displayName,
      description: role.description ?? '',
    },
  });

  useEffect(() => {
    reset({
      displayName: role.displayName,
      description: role.description ?? '',
    });
  }, [role.id, role.displayName, role.description, reset]);

  // Bubble dirty/busy up to the drawer-level guard. `isDirty` here uses
  // RHF's diff against the current `defaultValues` (which we re-`reset` on
  // every successful patch via TanStack invalidation → useRole refetch),
  // so reverting a typed change to baseline marks the form clean again.
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);
  // Reset dirty on unmount (tab switch / drawer close) so the parent guard
  // does not keep a stale-dirty signal for an already-discarded local form.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => {
    onBusyChange(patch.isPending);
  }, [patch.isPending, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  const onSubmit = (values: RoleEditFormValues) => {
    const payload: PatchRoleInput = {};
    if (values.displayName !== role.displayName) {
      payload.displayName = values.displayName;
    }
    const nextDescription = values.description === '' ? null : values.description;
    if (nextDescription !== role.description) {
      payload.description = nextDescription;
    }
    if (Object.keys(payload).length === 0) return;
    patch.mutate(payload, {
      onSuccess: () => showToast({ tone: 'success', text: t('roles:toasts.updated') }),
      onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormRow label={t('roles:fields.identifier')}>
          <Input value={role.name} readOnly monospace />
        </FormRow>
        <FormRow label={t('roles:fields.displayName')} error={errors.displayName?.message}>
          <Controller
            control={control}
            name="displayName"
            render={({ field }) => (
              <Input
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.displayName}
                readOnly={disabled}
              />
            )}
          />
        </FormRow>
        <FormRow label={t('roles:fields.description')}>
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Textarea
                value={field.value ?? ''}
                onChange={field.onChange}
                rows={3}
                readOnly={disabled}
              />
            )}
          />
        </FormRow>
        {!disabled && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="submit" kind="primary" disabled={!isDirty || patch.isPending}>
              {t('common:actions.save')}
            </Button>
          </div>
        )}
      </form>
      {!disabled && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: '1px solid var(--border-2)',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          {role.archived ? (
            <Button kind="ghost" onClick={() => setRestoreOpen(true)}>
              {t('common:actions.restore')}
            </Button>
          ) : (
            <Button kind="ghost" onClick={() => setArchiveOpen(true)} disabled={archiveBlocked}>
              {archiveBlocked ? t('roles:actions.archiveSystem') : t('common:actions.archive')}
            </Button>
          )}
        </div>
      )}
      {archiveOpen && <RoleArchiveConfirm role={role} onClose={() => setArchiveOpen(false)} />}
      {restoreOpen && <RoleRestoreConfirm role={role} onClose={() => setRestoreOpen(false)} />}
    </>
  );
}
